/* eslint-disable max-lines-per-function */
import { useNavigate } from 'react-router-dom';
import { Link } from '../../../components';
import { useTranslation } from 'react-i18next';
import { confirmSignIn } from 'aws-amplify/auth';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import * as Sentry from '@sentry/react';
import { useAppForm } from '@/components/form';
import { useState } from 'react';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { Alert, AlertDescription, Button } from '@e2n/ui';

type AuthErrorMessage =
  | {
      event: string;
      data?: unknown;
      message?: string;
    }
  | undefined;

function ErrorMessage({
  authErrorMessage,
}: {
  authErrorMessage: AuthErrorMessage;
}) {
  const { t } = useTranslation();
  const message =
    authErrorMessage?.message ?? t('pages.login.mfaForm.unknownError');
  return (
    <Alert data-testid="login-error-callout" variant="destructive">
      <AlertDescription>
        {message}{' '}
        <Link
          className="hover:text-primary-red-600"
          href="https://hilfe.e2n.de"
          target="_blank">
          {t('pages.login.loginForm.linkTitle')}
        </Link>
        {'.'}
      </AlertDescription>
    </Alert>
  );
}

export function MFAForm({ autofocus }: { autofocus: boolean }) {
  const [authErrorMessages, setAuthErrorMessages] =
    useState<AuthErrorMessage>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const form = useAppForm({
    defaultValues: { code: '' },
    onSubmit: async ({ value }) => {
      try {
        await confirmSignIn({ challengeResponse: value.code });
        navigate({ pathname: '/' }, { replace: true });
      } catch (e) {
        Sentry.captureException(e);
        setAuthErrorMessages({
          event: 'totp_failure',
          message: t('pages.login.mfaForm.totpCodeNotValid'),
        });
      }
    },
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      autoComplete="off">
      {authErrorMessages && (
        <ErrorMessage authErrorMessage={authErrorMessages} />
      )}

      <form.AppField name="code">
        {(field) => (
          <InputOTP
            id="mfa-code"
            autoFocus={autofocus}
            autoComplete="one-time-code"
            maxLength={6}
            value={field.state.value}
            pattern={REGEXP_ONLY_DIGITS}
            onChange={(value) => field.setValue(value)}>
            <InputOTPGroup className="w-full *:data-[slot=input-otp-slot]:flex-1 *:data-[slot=input-otp-slot]:h-12 *:data-[slot=input-otp-slot]:text-xl *:data-[slot=input-otp-slot]:border-neutrals-300 p-1">
              <InputOTPSlot index={0} data-testid="login-totp-input-0" />
              <InputOTPSlot index={1} data-testid="login-totp-input-1" />
              <InputOTPSlot index={2} data-testid="login-totp-input-2" />
              <InputOTPSlot index={3} data-testid="login-totp-input-3" />
              <InputOTPSlot index={4} data-testid="login-totp-input-4" />
              <InputOTPSlot index={5} data-testid="login-totp-input-5" />
            </InputOTPGroup>
          </InputOTP>
        )}
      </form.AppField>

      <form.AppForm>
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Button
              data-testid="login-mfa-submit-button"
              type="submit"
              size="sm"
              className="w-full justify-center gap-4"
              disabled={isSubmitting}>
              {isSubmitting
                ? t('pages.login.mfaForm.isSubmitting')
                : t('pages.login.mfaForm.buttonLabel')}
            </Button>
          )}
        </form.Subscribe>
      </form.AppForm>
    </form>
  );
}
